import { GOOGLE_MAP_API_KEY } from '../../../../Constants';
import {
  GoogleMap,
  MarkerF,
  PolylineF,
  useLoadScript,
} from "@react-google-maps/api";
import styles from "./TrackMap.module.css";

const TrackMap = ({ data, activeTab, height = "70vh", err }) => {
  // This will be undefined until the map script is fully loaded.
  const key = GOOGLE_MAP_API_KEY;

  // Load Google Maps API and necessary libraries
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: key,
    libraries: ["geometry"], // Make sure the 'geometry' library is loaded
  });

  if (!key) return <div>Missing Google Maps API key</div>;
  if (loadError) return <div>Error loading Google Maps</div>;
  if (!isLoaded) return <div>Loading…</div>;

  const routePath =
    (isLoaded &&
      data?.actual_route &&
      data?.actual_route.length > 0)
      ? data?.actual_route : (data?.actualRoute || []);


  const legendMap = [
    {
      label: "Origin",
      uri: "http://maps.google.com/mapfiles/ms/icons/green-dot.png",
    },
    {
      label: "Destination",
      uri: "http://maps.google.com/mapfiles/ms/icons/red-dot.png",
    },
    {
      label: "Last Known Location",
      uri: "http://maps.google.com/mapfiles/ms/icons/blue-dot.png",
    },
    {
      label: "Covered Locations",
      uri: "http://maps.google.com/mapfiles/ms/icons/orange-dot.png",
    },
  ];

  const origin = data?.origin?.location
    ? {
      coordinates: {
        lat: Number(data.origin.location.lat),
        lng: Number(data.origin.location.lon),
      },
      location_name: data.origin.location_name || data.origin.location.label,
    }
    : null;

  const destination = data?.destination?.location
    ? {
      coordinates: {
        lat: Number(data.destination.location.lat),
        lng: Number(data.destination.location.lon),
      },
      location_name:
        data.destination.location_name || data.destination.location.label,
    }
    : null;

  const pings = ((data?.["truckLocs"]?.length > 0 && data?.["truckLocs"]) || [])
    ?.map((t) => {
      if (t?.latitude && t?.longitude) {
        return {
          coordinates: {
            lat: Number(t.latitude),
            lng: Number(t.longitude),
          },
          location_name: t?.location || "Unknown Location",
        };
      }
      return null;
    })
    .filter(Boolean);

  //  reversing pings to maintain correct order
  const allPoints = [
    origin?.coordinates,
    ...pings?.reverse()?.map((p) => p.coordinates),
    destination?.coordinates,
  ].filter(Boolean);

  /* 
    Calculate the center based on origin and destination coordinates. 
    Fallback for when coordinates are missing is India's center 
  */
  const defaultCenter = { lat: 20.5937, lng: 78.9629 };
  let center = defaultCenter;

  if (origin?.coordinates && destination?.coordinates) {
    center = {
      lat: (origin.coordinates.lat + destination.coordinates.lat) / 2,
      lng: (origin.coordinates.lng + destination.coordinates.lng) / 2,
    };
  } else if (origin?.coordinates) {
    center = origin.coordinates;
  } else if (destination?.coordinates) {
    center = destination.coordinates;
  }

  const onLoad = (map) => {
    if (!allPoints.length) return;
    const bounds = new window.google.maps.LatLngBounds();
    allPoints.forEach((p) => bounds.extend(p));
  };

  return (
    <div style={{ height: height, width: "100%", position: "relative" }}>
      {err && <div className={styles.error}>{err}</div>}
      <GoogleMap
        onLoad={onLoad}
        mapContainerStyle={{ height: "100%", width: "100%" }}
        center={center}
        zoom={5.2}
        options={{
          disableDefaultUI: true,
          zoomControl: false,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
          rotateControl: false,
          scaleControl: false,
          draggable: true,
          scrollwheel: true,
          gestureHandling: "greedy",
          clickableIcons: false,
          tilt: 0,
          heading: 0,
          keyboardShortcuts: false,
        }}
      >
        {routePath && routePath.length > 1 && (
          <>
            <PolylineF
              path={routePath}
              options={{
                strokeColor: "#0F53FF", // Main line color
                strokeOpacity: 1,
                strokeWeight: 3, // Thinner line for the main polyline
                geodesic: false,
                zIndex: 101, // In front of the border polyline
                icons: [
                  {
                    icon: {
                      path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                      scale: 3,
                      strokeColor: "black",
                      strokeWeight: 2,
                    },
                    offset: "200px ",
                    repeat: "100px",
                  },
                ],
              }}
            />
          </>
        )}

        {origin && (
          <MarkerF
            position={origin.coordinates}
            title={origin.location_name || "Origin"}
            zIndex={10}
            icon={{
              url: "http://maps.google.com/mapfiles/ms/icons/green-dot.png",
            }}
          />
        )}

        {destination && (
          <MarkerF
            position={destination.coordinates}
            title={destination.location_name || "Destination"}
            zIndex={10}
            icon={{
              url: "http://maps.google.com/mapfiles/ms/icons/red-dot.png",
            }}
          />
        )}

        {pings?.length > 0 &&
          pings?.map((pt, i) => (
            <MarkerF
              key={i}
              position={pt.coordinates}
              title={
                i === pings?.length - 1 && data?.status?.length < 4
                  ? `Current Location: ${pt.location_name || ""}`
                  : pt.location_name || `Ping ${i + 1}`
              }
              zIndex={i === 0 ? 4 : 2}
              icon={{
                url:
                  i === pings?.length - 1 && data?.status?.length < 4
                    ? "http://maps.google.com/mapfiles/ms/icons/blue-dot.png" //current loc
                    : "http://maps.google.com/mapfiles/ms/icons/orange-dot.png", // already covered loc
                scaledSize:
                  i === pings?.length - 1 && data?.status?.length < 4
                    ? new google.maps.Size(32, 32)
                    : new google.maps.Size(20, 20),
              }}
            />
          ))}
      </GoogleMap>

      {/* Legend */}
      <div
        style={{
          position: "absolute",
          bottom: "5px",
          right: "5px",
          background: "white",
          padding: "2px 4px",
          borderRadius: "4px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
          fontSize: "15px",
          lineHeight: "1.2",
        }}
      >
        {legendMap?.map((m, i) => (
          <div
            className="mb-1"
            key={i}
            style={{ display: "flex", alignItems: "center" }}
          >
            <img
              src={m?.uri}
              alt={m?.label}
              style={{ width: "15px", height: "15px", marginRight: "4px" }}
            />
            <span>{m?.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TrackMap;
